/**
 * 伝票の取消を扱う唯一の入口。
 *
 * この業界では伝票を飛ばすこと自体は日常なので禁止はしない。
 * ただし「いつ・誰が・どの伝票を・なぜ」を必ず残し、
 * 押す前に「何が起きるか」を全部見せる。
 * 現場が知りたいのは伝票番号がどうなるかで、
 * それが分からないから「消していいのか分からない」まま操作される。
 *
 * 番号は詰めない。欠番として残す。詰めると後から何も分からなくなる。
 */

export type VoidReasonCode =
  | 'input_error'      // 打ち間違い
  | 'order_cancelled'  // 客が注文を取り消した
  | 'duplicate'        // 二重に打った
  | 'reissue'          // 打ち直す
  | 'test'             // 動作確認
  | 'other';           // その他

export const VOID_REASON_LABELS: Record<VoidReasonCode, string> = {
  input_error: '打ち間違い',
  order_cancelled: 'お客様が注文を取り消した',
  duplicate: '二重に打った',
  reissue: '打ち直す（取り消して入れ直す）',
  test: '動作確認',
  other: 'その他',
};

export const VOID_REASON_DESCRIPTIONS: Record<VoidReasonCode, string> = {
  input_error: '金額や商品を間違えて入れた場合。売上とバックから取り消されます。',
  order_cancelled: 'お客様の都合で注文が無くなった場合。売上とバックから取り消されます。',
  duplicate: '同じ伝票を2回入れてしまった場合。あとから入れた方を取り消してください。',
  reissue: '取り消したあと、正しい内容で入れ直します。新しい伝票番号が付きます。',
  test: '動作を試すために入れた伝票。売上に入れたくない場合に使います。',
  other: '上のどれにも当てはまらない場合。何があったかを必ず書いてください。',
};

export interface VoidPolicy {
  /** 取り消してよい役職 */
  allowedRoles: string[];
  /** 責任者の承認を挟むか */
  requireApproval: boolean;
  /** 会計から何分まで消せるか。null は制限なし */
  timeLimitMinutes: number | null;
  /** 領収書を出したあとも消せるか */
  allowAfterReceipt: boolean;
  /** メモを必須にするか */
  requireNote: boolean;
  /** 締めたあとの営業日も消せるか */
  allowAfterDailyClose: boolean;
}

export const DEFAULT_VOID_POLICY: VoidPolicy = {
  allowedRoles: ['owner', 'manager'],
  requireApproval: false,
  timeLimitMinutes: null,
  allowAfterReceipt: false,
  requireNote: true,
  allowAfterDailyClose: false,
};

/** 取り消す対象の伝票 */
export interface VoidTarget {
  id: string;
  type: 'order' | 'checkout';
  slipNumber: string | null;
  total: number;
  itemCount: number;
  businessDate: string;
  checkoutAt: Date;
  /** 領収書を発行済みか */
  receiptIssued: boolean;
  /** その営業日を締めたあとか */
  afterDailyClose: boolean;
  /** この伝票で発生したバックの合計。0 なら影響なし */
  backAmount: number;
}

export interface VoidCheckResult {
  allowed: boolean;
  /** 通らない理由。1つでもあれば取り消せない */
  blockers: string[];
  /** 通るが知らせるべきこと */
  warnings: string[];
  /** 承認待ちになるか */
  needsApproval: boolean;
}

/**
 * 取り消してよいかを決める。画面と保存で同じ判定を使う。
 * 判定を2箇所に持つと、画面では押せるのに保存で弾かれる、が起きる。
 */
export function checkVoidAllowed(
  target: VoidTarget,
  opts: { userRole: string; policy?: Partial<VoidPolicy>; now?: Date }
): VoidCheckResult {
  const p: VoidPolicy = { ...DEFAULT_VOID_POLICY, ...(opts.policy ?? {}) };
  const now = opts.now ?? new Date();
  const blockers: string[] = [];
  const warnings: string[] = [];

  if (!p.allowedRoles.includes(opts.userRole)) {
    blockers.push(
      `取り消しは ${p.allowedRoles.map(roleLabel).join('・')} だけができます。` +
      `いまの権限は「${roleLabel(opts.userRole)}」です。`
    );
  }

  if (p.timeLimitMinutes != null) {
    const min = Math.floor((now.getTime() - target.checkoutAt.getTime()) / 60000);
    if (min > p.timeLimitMinutes) {
      blockers.push(
        `会計から ${p.timeLimitMinutes} 分を過ぎた伝票は取り消せません（経過 ${min} 分）。` +
        `店舗設定で変えられます。`
      );
    }
  }

  if (target.receiptIssued && !p.allowAfterReceipt) {
    blockers.push(
      '領収書を発行済みの伝票です。お客様の手元に控えが残るため、既定では取り消せません。' +
      '店舗設定で許可できます。'
    );
  }

  if (target.afterDailyClose && !p.allowAfterDailyClose) {
    blockers.push(
      `${target.businessDate} は締めが終わっています。締めたあとの伝票は既定では取り消せません。`
    );
  }

  if (target.backAmount > 0) {
    warnings.push(
      `この伝票で ¥${Math.round(target.backAmount).toLocaleString()} のバックが発生しています。` +
      `取り消すと、その分がキャストの取り分から引かれます。締めた後だと差額の調整が要ります。`
    );
  }

  return {
    allowed: blockers.length === 0,
    blockers,
    warnings,
    needsApproval: p.requireApproval,
  };
}

export interface VoidEffect {
  slipNumberAfter: string;
  salesEffect: string;
  backEffect: string;
  receiptEffect: string;
  recordEffect: string;
}

/** 取り消すと何が起きるかを、そのまま画面に出せる言葉で返す */
export function describeVoidEffect(target: VoidTarget, reasonCode: VoidReasonCode): VoidEffect {
  const num = target.slipNumber ?? '（番号なし）';
  return {
    slipNumberAfter:
      reasonCode === 'reissue'
        ? `${num} は欠番として残ります。入れ直すと新しい番号が付きます。番号は詰めません。`
        : `${num} は欠番として残ります。あとの伝票の番号は詰めません。`,
    salesEffect:
      `売上から ¥${Math.round(target.total).toLocaleString()} が引かれます。` +
      `${target.businessDate} の日報と月の集計に反映されます。`,
    backEffect:
      target.backAmount > 0
        ? `この伝票で付いた ¥${Math.round(target.backAmount).toLocaleString()} のバックも取り消されます。`
        : 'この伝票にバックは付いていないので、キャストの取り分は変わりません。',
    receiptEffect:
      target.receiptIssued
        ? '領収書は発行済みです。お客様の控えは回収できないので、必要なら別途ご対応ください。'
        : '領収書はまだ出ていません。',
    recordEffect:
      '取り消した人・時刻・理由が記録に残ります。この記録は誰も消せません。',
  };
}

function roleLabel(role: string): string {
  return { owner: 'オーナー', manager: '店長', staff: 'スタッフ', parttime: 'アルバイト' }[role] ?? role;
}
